import PropTypes from "prop-types";
import React from "react";
import ENV from "app/utils/ENV";
import { View, ActivityIndicator } from "react-native";
import valueHelpers from "app/utils/valueHelpers";
import momentHelpers from "app/utils/momentHelpers";
import textHelpers from "app/utils/textHelpers";
import clipboardHelpers from "app/utils/clipboardHelpers";
import richTextHelpers from "app/utils/richTextHelpers";
import colorHelpers from "app/utils/colorHelpers";
import diffHelpers from "app/utils/diffHelpers";
import valueActions from "app/utils/valueActions";

import TextInput from "app/components/TextInput";
import Text from "app/components/Text";
import RichText from "app/components/RichText";
import VersionedLine from "app/components/VersionedLine";
import RichTextInput from "app/components/RichTextInput";
import Autocomplete from "app/components/Autocomplete";
import MultiButton from "app/components/MultiButton";
import Hoverable from "app/components/Hoverable";

import { StyleContext } from "app/contexts/Style";

export default class RichTextArea extends React.Component {
  static contextType = StyleContext;
  constructor(props) {
    super(props);
    this.state = {
      // text: props.text,
      selectedLines: [],
      selectingDirection: null,
      mouseDownOnLine: null,
      mouseHoverOnLine: null,
      composeMenuOpen: false,
      loading: false,
      undoAction: null,
      searchQuery: null,
      focusedLine: -1,
      diffLines: null,
      expandedFolds: {},
    };
  }

  componentDidMount() {
    if (ENV.WEB) document.addEventListener("keydown", this.handleDocKeyDown);
  }

  componentWillUnmount() {
    if (ENV.WEB)
      document.removeEventListener("keydown", this.handleDocKeyDown);
  }

  // catches selection-mode keys (copy/cut/strike/delete/arrows) once the
  // focused line has stopped editing and no input holds keyboard focus
  handleDocKeyDown = (e) => {
    if (!this.state.selectedLines?.length) return;
    let tag = document.activeElement?.tagName;
    // a focused input already routes keys through handleKeyDown; skip to
    // avoid handling the same event twice
    if (tag == "TEXTAREA" || tag == "INPUT") return;
    this.handleKeyDown(e);
  };

  handleKeyDown = (e) => {
    let { selectedLines, selectingDirection, focusedLine, undoAction } =
      this.state;
    console.log("rta keyhdown", e.key);
    if (
      e.metaKey &&
      e.shiftKey &&
      e.key == "Enter" &&
      this.props.onMetaShiftEnter
    ) {
      this.props.onMetaShiftEnter();
    } else if (e.metaKey && e.key == "Enter" && selectedLines.length > 1) {
      this.handleComposeFromSelected();
    } else if (e.metaKey && e.key == "Enter" && this.props.onMetaEnter) {
      this.props.onMetaEnter();
    } else if (
      e.metaKey &&
      e.key == "Backspace" &&
      this.props.onMetaBackspace
    ) {
      this.props.onMetaBackspace();
    } else if (e.metaKey && e.key == "a") {
      this.selectAll();
      e.preventDefault();
      return true;
    } else if (e.metaKey && e.key == "z" && !!undoAction) {
      undoAction();
      e.preventDefault();
      this.setState({ undoAction: null });
      return true;
    } else if (
      e.metaKey &&
      e.key == "k" &&
      focusedLine >= 0 &&
      selectedLines.length <= 1
    ) {
      this.strikeLines([focusedLine]);
      e.preventDefault();
      return true;
    } else if (
      (selectedLines.length > 1 || e.shiftKey) &&
      ["ArrowUp", "ArrowDown"].includes(e.key)
    ) {
      if (e.key == "ArrowUp") {
        if (selectingDirection == "down") {
          this.unselectFromBottom();
        } else {
          this.onSelectUp();
        }
        e.preventDefault();
        return true;
      } else if (e.key == "ArrowDown") {
        if (selectingDirection == "up") {
          this.unselectFromTop();
        } else {
          this.onSelectDown();
        }
        e.preventDefault();
        return true;
      }
    } else if (selectedLines.length > 1) {
      console.log("rta keydown:", e.key, selectedLines);
      if (e.metaKey && e.key == "x") {
        this.cutSelectedLines();
        e.preventDefault();
        return true;
      } else if (e.metaKey && e.key == "c") {
        this.copySelectedLines();
        e.preventDefault();
        return true;
      } else if (e.metaKey && e.key == "k") {
        this.strikeLines(selectedLines);
        e.preventDefault();
        return true;
      } else if (e.key == "Escape") {
        this.stopSelecting();
        return true;
      } else if (e.key == "ArrowRight") {
        return;
        //   this.stopSelecting(999);
      } else if (e.key == "ArrowLeft") {
        return;
        //   this.stopSelecting(0);
      } else if (e.key == "Backspace" || e.key == "Delete") {
        if (!!selectedLines?.length) {
          this.deleteSelection();
          return true;
        }
      } else {
        console.error("continue", e.key);
        return false;
      }
      // not working, maybe it would need to be cancelled from within the child?
      e.stopPropagation();
      e.preventDefault();
      return true;
    }
  };

  focus = async (pos = 0) => {
    this.localFocus(pos);
  };

  localFocus = async (pos = 0) => {
    try {
      let { text } = this.props;
      let lines = text.split(/[\r\n]/);
      if (pos > 0 && this.state.focusedLine < 0) {
        // focus first, so we can target the bottom
        await this.setState({ focusedLine: lines.length - 1 });
        await new Promise((r) => setTimeout(r, 0));
      }
      let numberOfLines = lines.length;
      let caretPos = 0;
      if (pos >= numberOfLines) {
        pos = numberOfLines - 1;
        caretPos = 999;
      }
      let ref = null;
      ref = this[`ref-${pos}`];
      ref?.focus(caretPos);
    } catch (e) {
      console.warn("rta focus fail", e);
    }
  };

  render() {
    let {
      text,
      wrapperStyle,
      value,
      chapter,
      numberOfLines,
      snapshotText,
      nextSnapshotText,
    } = this.props;
    let {
      selectedLines,
      selectingDirection,
      loading,
      searchQuery,
      focusedLine,
      diffLines,
    } = this.state;

    if (!ENV.WEB && focusedLine >= 0) {
      return (
        <View style={[{ flex: 1 }, wrapperStyle]}>
          <TextInput
            multiline
            autoHeight
            autoFocus
            keyboardAccType={"scratchpad"}
            onChangeText={(text) => {
              this.props.onChangeText(text);
            }}
            inputRef={(r) => (this.inputRef = r)}
            ref={(r) => (this.ref = r)}
            onFocus={() => {
              this.context.setContext("focusedInputRef", this.ref);
              this.context.setContext("focusedValue", value);
              this.context.setContext("focusedChapter", chapter);
            }}
            onBlur={() => {
              console.log("onBlur");
              this.setState({ focusedLine: -1 });
              this.props.onStopEditing();
            }}
          >
            {text}
          </TextInput>
        </View>
      );
    }

    let lines = text.split(/[\r\n]/);
    // let snapshotLines =
    //   focusedLine >= 0 ? [] : snapshotText?.split(/[\r\n]/) || [];
    // let snapshotLinesOffset = 0;

    // Pre-process code block ranges
    let codeDelimiterSet = new Set();
    let codeContentSet = new Set();
    let codeBlockFirstLine = new Set();
    let codeBlockLastLine = new Set();
    {
      let inCB = false;
      let lastCBLine = null;
      lines.forEach((line, i) => {
        if (line.trim() === "```") {
          codeDelimiterSet.add(i);
          if (!inCB) {
            inCB = true;
            lastCBLine = null;
          } else {
            inCB = false;
            if (lastCBLine !== null) codeBlockLastLine.add(lastCBLine);
          }
        } else if (inCB) {
          codeContentSet.add(i);
          if (lastCBLine === null) codeBlockFirstLine.add(i);
          lastCBLine = i;
        }
      });
      if (inCB && lastCBLine !== null) codeBlockLastLine.add(lastCBLine);
    }

    let foldedLines =
      lines
        .find((l) => l.startsWith("foldedLines="))
        ?.split("=")[1]
        .split(",")
        .map((x) => parseInt(x)) || [];

    let prevItemFolded = false;
    let prevIndentation = 0;

    // Pre-process '> Title' fold markers.
    // Body: blank lines and indented lines (leading space/tab), until a non-empty non-indented line.
    // Trailing blank lines are excluded from the body (remain visible as separators).
    const foldMarkerSet = new Set();
    const foldBodyLineSet = new Set();       // for rendering (force-expands when title is focused)
    const foldBodyLineSetForNav = new Set(); // for navigation (expandedFolds only)
    {
      const { expandedFolds } = this.state;
      let fi = 0;
      while (fi < lines.length) {
        if (lines[fi].startsWith("> ")) {
          const markerIdx = fi;
          foldMarkerSet.add(markerIdx);
          fi++;
          // Collect all candidate body lines
          const allBody = [];
          while (fi < lines.length) {
            const l = lines[fi];
            if (l.startsWith("> ") || /^#{1,3} /.test(l)) break;
            if (l.length > 0 && !l.startsWith(" ") && !l.startsWith("\t")) break;
            allBody.push(fi);
            fi++;
          }
          // Trim trailing blank lines
          let lastNonEmpty = allBody.length - 1;
          while (lastNonEmpty >= 0 && lines[allBody[lastNonEmpty]] === "") lastNonEmpty--;
          const body = allBody.slice(0, lastNonEmpty + 1);
          const forceExpanded = !!expandedFolds[markerIdx] || focusedLine === markerIdx;
          if (!forceExpanded) body.forEach(idx => foldBodyLineSet.add(idx));
          if (!expandedFolds[markerIdx]) body.forEach(idx => foldBodyLineSetForNav.add(idx));
        } else {
          fi++;
        }
      }
    }

    if (!numberOfLines || focusedLine >= 0) {
      numberOfLines = lines.length;
    }
    // console.log("numberOfLines", numberOfLines);

    let valueColor =
      valueHelpers.getColor(value, this.context.colorScheme) ||
      this.context.colors.foreground;

    return (
      <View style={[{ flex: 1 }, wrapperStyle]}>
        {!text?.length && !!this.props.placeholder?.length && focusedLine < 0 && (
          <Text
            style={{
              position: "absolute",
              opacity: 0.4,
              left: this.context.sizes.small,
              top: 2,
            }}
            pointerEvents="none"
          >
            {this.props.placeholder}
          </Text>
        )}

        {focusedLine < 0 && !!diffLines?.length
          ? diffLines.map((line, i) => {
              return <VersionedLine key={i} line={line} color={valueColor} />;
            })
          : lines.map((line, i) => {
              if (!!numberOfLines && i >= numberOfLines) return null;
              let isDelimiterLine = codeDelimiterSet.has(i);
              let delimiterHidden = isDelimiterLine && focusedLine !== i;
              let selected = selectedLines.includes(i);
              let indentation = richTextHelpers.getIndentation(line);
              let nextIndentation = !!lines[i + 1]
                ? richTextHelpers.getIndentation(lines[i + 1])
                : 0;
              let canFold = indentation < nextIndentation;
              let folded = foldedLines.includes(i);
              let skip = false;
              // console.log(line, indentation, prevIndentation, prevItemFolded);
              if (indentation > prevIndentation && prevItemFolded) {
                skip = true;
                return null;
              } else {
                prevItemFolded = folded;
                prevIndentation = indentation;
              }

              // Skip body lines of collapsed '> Title' folds
              if (foldBodyLineSet.has(i)) return null;

              // let snapshotLine = snapshotLines[i + snapshotLinesOffset];
              let snapshotStatus = momentHelpers.getSnapshotLineStatus(
                line,
                snapshotText,
                nextSnapshotText,
                i,
              );

              let isCodeLine = codeContentSet.has(i);
              let isFirstCodeLine = codeBlockFirstLine.has(i);
              let isLastCodeLine = codeBlockLastLine.has(i);

              return (
                <View
                  key={i}
                  style={{
                    zIndex: selected ? lines.length + 1 : lines.length - i,
                    opacity: !selected && selectedLines?.length ? 0.3 : 1,
                    cursor: "text",
                    ...(delimiterHidden && { height: 0, overflow: "hidden" }),
                    ...(isCodeLine && {
                      backgroundColor: colorHelpers.hexToRgbaString(
                        this.context.colors.foreground,
                        0.08,
                      ),
                      paddingHorizontal: 8,
                      borderTopLeftRadius: isFirstCodeLine ? 4 : 0,
                      borderTopRightRadius: isFirstCodeLine ? 4 : 0,
                      borderBottomLeftRadius: isLastCodeLine ? 4 : 0,
                      borderBottomRightRadius: isLastCodeLine ? 4 : 0,
                      marginVertical: isFirstCodeLine ? 2 : 0,
                    }),
                  }}
                  onMouseEnter={(e) => {
                    // console.log("mouse enter", i);
                    if (this.state.mouseDownOnLine !== null) {
                      // this.setState({ mouseHoverOnLine: i });
                      // this.toggleSelected(i, true);
                      if (this.state.focusedLine >= 0) {
                        let focusedRef = this[`ref-${this.state.focusedLine}`];
                        focusedRef?.stopEditing(false);
                      }
                      window.getSelection().empty();
                      this.selectRange(this.state.mouseDownOnLine, i);
                    }
                  }}
                  // onMouseLeave={async (e) => {
                  //   await new Promise((r) => setTimeout(r, 0));
                  //   console.log("mouse leave", i);
                  //   if (this.state.mouseDownOnLine == i) {
                  //     window.getSelection().empty();
                  //     this.toggleSelected(i, true);
                  //   } else {
                  //     if (this.state.mouseHoverOnLine < i) {
                  //       // mouse went up
                  //       this.toggleSelected(i, true);
                  //     } else {
                  //       // mouse went back down
                  //       this.toggleSelected(i, false);
                  //     }
                  //   }
                  // }}
                  onMouseDown={(e) => {
                    this.setState({ mouseDownOnLine: i });
                  }}
                  onMouseUp={(e) => {
                    this.setState({ mouseDownOnLine: null });
                  }}
                >
                  {/*selected && i == selectedLines[0] && (
                <View style={this.styles().composerTooltipHat} />
              )}
              {selected && i == selectedLines[0] && (
                <View
                  style={[
                    this.styles().composerTooltipHat,
                    { backgroundColor: this.context.colors.highlight },
                  ]}
                />
              )*/}
                  <RichTextInput
                    lineIndex={i}
                    snapshotStatus={snapshotStatus}
                    readOnly={this.props.readOnly}
                    defaultFocus={this.props.defaultFocus && i == 0}
                    // hideAutocomplete={!!selectedLines?.length}
                    hideAutocomplete={
                      !!selectedLines?.length && selectedLines.length > 1
                    }
                    value={value}
                    chapter={chapter}
                    references={this.props.references || value?.references}
                    codeBlock={isCodeLine}
                    textStyle={isCodeLine ? { fontFamily: "Courier New", fontSize: 13 } : undefined}
                    // nativeID={
                    //   this.props.defaultFocus && i == 0
                    //     ? "default-focus"
                    //     : undefined
                    // }
                    selected={selected}
                    {...this.props}
                    // numberOfLines={numberOfLines}
                    foldExpanded={foldMarkerSet.has(i) ? (!!this.state.expandedFolds[i] || focusedLine === i) : undefined}
                    onToggleFold={foldMarkerSet.has(i) ? () => {
                      this.setState(prev => ({
                        expandedFolds: { ...prev.expandedFolds, [i]: !prev.expandedFolds[i] }
                      }));
                    } : undefined}
                    onStopEditing={(updateText) => {
                      if (!!selectedLines?.length) return;
                      console.log("stop editing");
                      setTimeout(() => {
                        // allow the next focusedLine to take priority first
                        let newFocusedLine = this.state.focusedLine;
                        if (this.state.focusedLine == i) newFocusedLine = -1;
                        this.setState({
                          focusedLine: newFocusedLine,
                          selectedLines: [],
                          selectingDirection: null,
                        });
                        if (updateText) this.props.onStopEditing();
                      }, 0);
                    }}
                    onFocus={() => {
                      this.setState({
                        selectedLines: [],
                        mouseDownOnLine: null,
                        mouseHoverOnLine: null,
                        composeMenuOpen: false,
                        focusedLine: i,
                      });
                      this.props.onFocus && this.props.onFocus();
                    }}
                    autoFocus
                    folded={folded}
                    canFold={canFold}
                    toggleFolded={() => {
                      if (!lines.find((l) => l.startsWith("foldedLines="))) {
                        lines.push("foldedLines=");
                      }
                      let fullText = lines
                        .map((l, j) => {
                          if (l.startsWith("foldedLines=")) {
                            if (foldedLines.includes(i)) {
                              foldedLines = foldedLines.filter((x) => x != i);
                            } else {
                              foldedLines.push(i);
                            }
                            return `foldedLines=${foldedLines.join(",")}`;
                          } else return l;
                        })
                        .join(`\n`);
                      this.props.onChangeText(fullText);
                    }}
                    hidden={skip}
                    ref={(r) => (this[`ref-${i}`] = r)}
                    inputRef={(r) => (this[`input-${i}`] = r)}
                    toggleSelected={(select) => {
                      this.toggleSelected(i, select);
                    }}
                    deselectAll={() => this.setState({ selectedLines: [] })}
                    onChangeText={(lineText) => {
                      let fullText = lines
                        .map((l, j) => {
                          if (j == i) return lineText;
                          else return l;
                        })
                        .join(`\n`);
                      this.props.onChangeText(fullText);
                    }}
                    onMetaUp={() => {
                      if (this.props.onMetaUp && i == 0) {
                        this.props.onMetaUp();
                        return;
                      }
                      let firstInputRef = this[`ref-${0}`];
                      !!firstInputRef && firstInputRef.focus(0);
                    }}
                    onMetaDown={() => {
                      if (this.props.onMetaDown && i == lines.length - 1) {
                        this.props.onMetaDown();
                        return;
                      }
                      let lastInputRef = this[`ref-${lines.length - 1}`];
                      !!lastInputRef && lastInputRef.focus(999);
                    }}
                    focusNext={(caretPos = 0) => {
                      let nextLine = i + 1;
                      while (nextLine < lines.length && foldBodyLineSetForNav.has(nextLine)) nextLine++;
                      if (nextLine >= lines.length) {
                        this.props.focusNext && this.props.focusNext(caretPos);
                        return;
                      }
                      let nextInputRef = this[`ref-${nextLine}`] || this[`input-${nextLine}`];
                      nextInputRef?.focus(caretPos);
                    }}
                    focusPrevious={(caretPos = 0) => {
                      let prevLine = i - 1;
                      while (prevLine >= 0 && foldBodyLineSetForNav.has(prevLine)) prevLine--;
                      if (prevLine < 0) {
                        this.props.focusPrevious && this.props.focusPrevious(caretPos);
                        return;
                      }
                      let prevInputRef = this[`input-${prevLine}`] || this[`ref-${prevLine}`];
                      try {
                        prevInputRef?.focus(caretPos);
                      } catch (e) {
                        console.error("CATCH FOCUS PREV ERROR", prevInputRef, e);
                      }
                    }}
                    onEnter={async (
                      caretPos = 0,
                      indentation = 0,
                      bullet = false,
                    ) => {
                      let newLine = "\n";
                      if (!!indentation) {
                        newLine += new Array(indentation).fill("\t").join("");
                        // let bullets = ["", "•", "◦", "▪︎", "▫︎"];
                        // newLine += bullets[indentation] + " ";
                      }
                      let fullText = lines
                        .map((l, j) => {
                          if (j == i) {
                            let newLineBody = l.slice(caretPos).trim();
                            let numbered = l.match(
                              richTextHelpers.numberedRegex,
                            );
                            if (bullet && !newLineBody.startsWith("- ")) {
                              newLineBody = `- ${newLineBody}`;
                            } else if (
                              !!numbered &&
                              !newLineBody.match(/^\d+[.)] /)
                            ) {
                              // continue numbered lists: "2. " after "1. "
                              newLineBody = `${parseInt(numbered[2]) + 1}${
                                numbered[3]
                              } ${newLineBody}`;
                            }
                            return `${l.slice(
                              0,
                              caretPos,
                            )}${newLine}${newLineBody}`;
                          } else return l;
                        })
                        .join(`\n`);
                      // console.log(
                      //   "onEnter",
                      //   `cp: ${caretPos}`,
                      //   `newline: ${newLine}`,
                      //   // `newline body: ${newLineBody}`
                      // );
                      this.props.onChangeText(fullText);
                      await new Promise((r) => setTimeout(r, 0));
                      let newInputRef =
                        this[`input-${i + 1}`] || this[`ref-${i + 1}`];
                      !!newInputRef && newInputRef.focus();
                    }}
                    onDeleteLine={async (focusNext = false) => {
                      let fullText = lines.filter((l, j) => j != i).join("\n");
                      this.props.onChangeText(fullText);
                      await new Promise((r) => setTimeout(r, 0));
                      if (focusNext) {
                        let inputRef = this[`ref-${i}`];
                        !!inputRef && inputRef.focus();
                      } else {
                        let inputRef = this[`ref-${i - 1}`] || this[`ref-${i}`];
                        !!inputRef && inputRef.focus(999);
                      }
                    }}
                    mergeLineUp={
                      i == 0
                        ? null
                        : async () => {
                            let startingPos = 0;
                            let fullText = lines
                              .map((l, j) => {
                                if (j == i - 1) {
                                  startingPos = l.length;
                                  return `${l}${line}`;
                                } else if (j == i) return null;
                                else return l;
                              })
                              .filter((l) => l !== null)
                              .join("\n");
                            this.props.onChangeText(fullText);
                            await new Promise((r) => setTimeout(r, 0));
                            let inputRef =
                              this[`ref-${i - 1}`] || this[`ref-${i}`];
                            !!inputRef && inputRef.focus(startingPos);
                          }
                    }
                    mergeNextLineUp={
                      i == lines.length - 1
                        ? null
                        : async () => {
                            let startingPos = 0;
                            let fullText = lines
                              .map((l, j) => {
                                if (j == i) {
                                  startingPos = l.length;
                                  return `${l}${lines[i + 1]}`;
                                } else if (j == i + 1) return null;
                                else return l;
                              })
                              .filter((l) => l !== null)
                              .join("\n");
                            this.props.onChangeText(fullText);
                            // await new Promise((r) => setTimeout(r, 0));
                            // let inputRef =
                            //   this[`ref-${i - 1}`] || this[`ref-${i}`];
                            // !!inputRef && inputRef.focus(startingPos);
                          }
                    }
                    selectUp={() => {
                      this.toggleSelected(i, true);
                      // this.onSelectUp();
                    }}
                    selectDown={() => {
                      this.toggleSelected(i, true);
                      // this.onSelectDown();
                    }}
                    swapLineUp={
                      i == 0
                        ? null
                        : async () => {
                            let fullText = lines
                              .map((l, j) => {
                                if (j == i - 1) {
                                  return line;
                                } else if (j == i) {
                                  return lines[j - 1];
                                }
                                return l;
                              })
                              .join("\n");
                            this.props.onChangeText(fullText);
                            await new Promise((r) => setTimeout(r, 0));
                            let inputRef =
                              this[`ref-${i - 1}`] || this[`ref-${i - 1}`];
                            !!inputRef && inputRef.focus();
                          }
                    }
                    swapLineDown={
                      i == lines.length - 1
                        ? null
                        : async () => {
                            let fullText = lines
                              .map((l, j) => {
                                if (j == i + 1) {
                                  return line;
                                } else if (j == i) {
                                  return lines[j + 1];
                                }
                                return l;
                              })
                              .join("\n");
                            this.props.onChangeText(fullText);
                            await new Promise((r) => setTimeout(r, 0));
                            let inputRef =
                              this[`ref-${i + 1}`] || this[`ref-${i + 1}`];
                            !!inputRef && inputRef.focus();
                          }
                    }
                    onIndent={() => {
                      let fullText = lines
                        .map((l, j) => (j == i ? `\t${l}` : l))
                        .join("\n");
                      this.props.onChangeText(fullText);
                    }}
                    onUnindent={() => {
                      let fullText = lines
                        .map((l, j) => {
                          if (j == i) {
                            if (l.startsWith("\t")) {
                              return l.slice(1);
                            }
                          }
                          return l;
                        })
                        .join("\n");
                      this.props.onChangeText(fullText);
                    }}
                    handleKeyDown={this.handleKeyDown}
                  >
                    {line}
                  </RichTextInput>

                  {selected &&
                    i == selectedLines[selectedLines.length - 1] &&
                    selectedLines.length > 1 && (
                      <View
                        style={[this.styles().composerTooltip]}
                        pointerEvents={
                          this.state.mouseDownOnLine !== null && "none"
                        }
                      >
                        <View
                          style={this.styles().composerTooltipContent}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Text
                            button
                            onPress={(e) =>
                              this.stopSelecting(
                                selectingDirection == "up" ? 0 : 999,
                              )
                            }
                            // onMouseDown={(e) => e.stopPropagation()}
                          >
                            Cancel
                          </Text>
                          {!this.state.composeMenuOpen ? (
                            <Text
                              button
                              onPress={() =>
                                this.setState({ composeMenuOpen: true })
                              }
                            >
                              Compose{" "}
                              {loading && <ActivityIndicator size="small" />}
                            </Text>
                          ) : (
                            <>
                              <Text
                                button
                                onPress={this.handleComposeFromSelected}
                              >
                                Create Child Value{" "}
                                {loading && <ActivityIndicator size="small" />}
                              </Text>
                              {!!this.props.onCopyToMoment && (
                                <Text
                                  button
                                  onPress={this.handleCopySelectedToMoment}
                                >
                                  {!!this.props.composeMoment?.id
                                    ? "Copy to Moment"
                                    : "Create Moment"}
                                </Text>
                              )}
                            </>
                          )}
                          <View
                            style={this.styles().composerTooltipBackground}
                          />
                          <View
                            style={[
                              this.styles().composerTooltipBackground,
                              {
                                backgroundColor: this.context.colors.highlight,
                              },
                            ]}
                          />
                        </View>
                      </View>
                    )}
                </View>
              );
            })}
      </View>
    );
  }

  toggleSelected = (lineIndex, select) => {
    let selectedLines = [...this.state.selectedLines];
    if (select) {
      selectedLines.push(lineIndex);
    } else {
      if (selectedLines.includes(lineIndex)) {
        selectedLines.splice(selectedLines.indexOf(lineIndex), 1);
      }
    }
    console.log("select down", lineIndex, select);
    setTimeout(() => {
      this.setState({ selectedLines: textHelpers.uniq(selectedLines).sort() });
    }, 0);
  };

  deleteSelection = (undoAction = null) => {
    let { selectedLines, selectingDirection } = this.state;
    let { text } = this.props;
    let lines = text.split(/[\r\n]/);
    let removedLines = [];
    let fullText = lines
      .map((l, j) => {
        if (selectedLines.includes(j)) {
          removedLines.push(l);
          if (j == selectedLines[0]) return "";
          else return null;
        }
        return l;
      })
      .filter((x) => !!x || x == "")
      .join("\n");
    this.props.onChangeText(fullText);

    let ref;
    // if (selectingDirection == "up") {
    ref = this[`ref-${selectedLines[0]}`];
    // } else {
    //   ref = this[`ref-${selectedLines[Math.max(selectedLines.length - 2, 0)]}`];
    // }

    if (!undoAction) {
      const textBeforeDelete = text;
      undoAction = () => {
        this.props.onChangeText(textBeforeDelete);
      };
    }

    this.setState({
      selectedLines: [],
      selectingDirection: null,
      composeMenuOpen: false,
      undoAction,
    });
    ref?.focus();
  };

  copySelectedLines = async () => {
    let { selectedLines } = this.state;
    let { text } = this.props;
    let lines = text.split(/[\r\n]/);

    let textToCopy = lines
      .filter((l, i) => selectedLines.includes(i))
      .join("\n");
    clipboardHelpers.copy(textToCopy);
  };

  cutSelectedLines = async () => {
    await this.copySelectedLines();

    const textBeforeCut = this.props.text;
    const undoAction = () => {
      this.props.onChangeText(textBeforeCut);
    };

    this.deleteSelection(undoAction);
  };

  strikeLines = async (targteLines = []) => {
    console.log("strike lines", targteLines);
    let { selectedLines, selectingDirection } = this.state;
    if (!targteLines.length || targteLines[0] < 0) targteLines = selectedLines;
    let { text } = this.props;
    let lines = text.split(/[\r\n]/);

    let anchorLine = targteLines[0];
    if (selectingDirection == "up") {
      anchorLine = targteLines[targteLines.length - 1];
    }
    let shouldStrike = !lines[anchorLine]?.includes("~");

    let fullText = lines
      .map((l, j) => {
        if (!targteLines.includes(j)) return l;
        else if (!shouldStrike) return l.replace(/\~/g, "");
        else if (l.includes("~")) return l;
        else {
          let startingIndex = richTextHelpers.findStartingIndex(l);
          let struckLine = `${l.slice(0, startingIndex)}~${l.slice(
            startingIndex,
          )}~`;
          return struckLine;
        }
      })
      .join(`\n`);

    let undoAction = () => this.props.onChangeText(text);
    this.setState({
      selectedLines: [],
      selectingDirection: null,
      composeMenuOpen: false,
      undoAction,
    });

    this.props.onChangeText(fullText);
  };

  onSelectUp = () => {
    let { selectedLines, focusedLine } = this.state;
    if (!selectedLines.length && focusedLine >= 0) {
      selectedLines = [focusedLine];
    }
    if (!!selectedLines[0]) {
      selectedLines.push(selectedLines[0] - 1);
    }
    this.setState({
      selectedLines: textHelpers.uniq(selectedLines).sort((a, b) => a - b),
      selectingDirection: "up",
    });
  };

  onSelectDown = () => {
    let { selectedLines, focusedLine } = this.state;
    let lastSelectedLine;
    if (!selectedLines.length && focusedLine >= 0) {
      selectedLines = [focusedLine];
    }
    if (selectedLines.length) {
      let lines = this.props.text?.split(/[\r\n]/);
      lastSelectedLine = selectedLines[selectedLines.length - 1];
      console.log(selectedLines, lastSelectedLine, lines.length - 1);
      if (lastSelectedLine != lines.length - 1) {
        selectedLines.push(lastSelectedLine + 1);
      }
    }
    this.setState({
      selectedLines: textHelpers.uniq(selectedLines).sort((a, b) => a - b),
      selectingDirection: "down",
    });
  };

  unselectFromTop = () => {
    let { selectedLines, selectingDirection } = this.state;
    if (selectedLines.length > 1) {
      selectedLines.splice(0, 1);
    }
    selectedLines = textHelpers.uniq(selectedLines).sort((a, b) => a - b);
    selectingDirection = selectedLines.length == 1 ? null : selectingDirection;
    this.setState({ selectedLines, selectingDirection });
  };

  unselectFromBottom = () => {
    let { selectedLines, selectingDirection } = this.state;
    if (selectedLines.length > 1) {
      selectedLines.splice(selectedLines.length - 1, 1);
    }
    selectedLines = textHelpers.uniq(selectedLines).sort((a, b) => a - b);
    selectingDirection = selectedLines.length == 1 ? null : selectingDirection;
    this.setState({ selectedLines, selectingDirection });
  };

  stopSelecting = (caretPos = undefined) => {
    let { selectedLines, selectingDirection } = this.state;
    let selection = [0, selectedLines.length == 1 ? 999 : undefined];

    let lineToFocus =
      selectingDirection == "down"
        ? selectedLines[selectedLines.length - 1]
        : selectedLines[0];

    if (selectingDirection == "down") {
      if (caretPos == 0) {
        lineToFocus = selectedLines[0];
      } else {
        lineToFocus = selectedLines[selectedLines.length - 1];
      }
    } else if (selectingDirection == "up") {
      if (caretPos == 0) {
        lineToFocus = selectedLines[0];
      } else {
        lineToFocus = selectedLines[selectedLines.length - 1];
      }
    }

    this.setState({
      selectedLines: [],
      mouseDownOnLine: null,
      mouseHoverOnLine: null,
      composeMenuOpen: false,
    });
    let focusRef = this[`ref-${lineToFocus}`];
    if (caretPos == undefined) {
      focusRef?.focus(selection[0], selection[1]);
    } else {
      focusRef?.focus(caretPos);
    }
  };

  selectRange = (start, end) => {
    let selectedLines = [];
    let selectingDirection = null;
    let lineNumbers = this.props.text.split(/[\r\n]/).map((x, i) => i);
    if (start == end) {
      selectedLines = [start];
    } else if (start > end) {
      selectedLines = lineNumbers.filter((n) => n >= end && n <= start);
      selectingDirection = "up";
    } else if (start < end) {
      selectedLines = lineNumbers.filter((n) => n >= start && n <= end);
      selectingDirection = "down";
    }
    // console.log(selectingDirection);
    this.setState({ selectedLines, selectingDirection });
  };

  selectAll = () => {
    let lines = this.props.text.split(/[\r\n]/);
    let selectedLines = lines.map((x, i) => i);
    this.setState({ selectedLines });
    // drop the focused line out of edit mode so it renders with the
    // selection highlight like the other lines (matches shift+arrow flow)
    if (this.state.focusedLine >= 0) {
      let focusedRef = this[`ref-${this.state.focusedLine}`];
      focusedRef?.stopEditing(false);
    }
  };

  handleComposeFromSelected = async () => {
    let { value, chapter, text } = this.props;
    let { loading, selectedLines } = this.state;
    if (loading) return;
    let lines = text.split(/[\r\n]/);
    console.log("COMPOSE!");
    this.setState({ loading: true });

    let newTitle = lines[selectedLines[0]];
    let titleObj = richTextHelpers.getLineObject(newTitle);
    // remove indentation and bullets from title
    newTitle = titleObj.body;
    let titleIndentation = titleObj.indentation;
    let titleBullet = titleObj.bullet;

    let newText;
    let bodyLines = lines.filter((l, i) => selectedLines.indexOf(i) >= 1);
    if (bodyLines.length == 1) {
      // remove indentation and bullets if only 1 line
      let lineObj = richTextHelpers.getLineObject(bodyLines[0]);
      newText = lineObj.body;
    } else {
      bodyLines = bodyLines.map((l) => {
        let lineObj = richTextHelpers.getLineObject(l);
        let indentation = lineObj.indentation - titleIndentation;
        let formattedLine = "";
        for (let i = 0; i < indentation; i++) {
          formattedLine += `\t`;
        }
        if (lineObj.bullet) {
          formattedLine += `- `;
        }
        console.log(indentation, formattedLine);
        formattedLine += lineObj.body;
        return formattedLine;
      });
      newText = bodyLines.join("\n");
    }

    let trimmedLines = lines.filter((l, i) => !selectedLines.includes(i));

    let child = await valueActions.create(
      {
        title: newTitle,
        text: newText,
        parent_ids: !!value?.id ? [value.id] : [],
      },
      chapter?.id,
    );
    console.log("created", child);
    if (!!child?.id) {
      let refParams = "";
      if (selectedLines.length > 1) {
        // refParams = "-expanded";
      }
      let refPrefix = "";
      for (let i = 0; i < titleIndentation; i++) {
        refPrefix += `\t`;
      }
      if (!!titleBullet) {
        refPrefix += "- ";
      }
      trimmedLines.splice(
        selectedLines[0],
        0,
        `${refPrefix}{${child.id}${refParams}}`,
      );
      let trimmedText = trimmedLines.join("\n");
      this.props.onChangeText(trimmedText);
      this[`ref-${selectedLines[0]}`]?.focus();
    }
    this.setState({ loading: false, selectedLines: [], composeMenuOpen: false });
  };

  // sends the selected lines' text to the hosting scratchpad so it can copy
  // them into the viewed day's moment (or create one); selection is kept on
  // failure so the user can retry
  handleCopySelectedToMoment = async () => {
    let { text } = this.props;
    let { loading, selectedLines, selectingDirection } = this.state;
    if (loading) return;
    this.setState({ loading: true });
    let lines = text.split(/[\r\n]/);
    let selectedText = lines
      .filter((l, i) => selectedLines.includes(i))
      .join("\n");
    let ok = await this.props.onCopyToMoment(selectedText);
    this.setState({ loading: false });
    if (ok !== false) {
      this.stopSelecting(selectingDirection == "up" ? 0 : 999);
    }
  };

  styles = (style = { fontSize: this.context.sizes.default }) => {
    let fontSize = style.fontSize;
    let lh = 1.6;
    return {
      row: {
        flexDirection: "row",
        flexWrap: "wrap",
      },
      text: {
        fontSize: style.fontSize,
        lineHeight: style.fontSize * lh,
        // wordWrap: "none",
        cursor: "text",
        overflow: "hidden",
      },
      header: {
        marginBottom: style.fontSize * 0.4,
      },
      header1: {
        fontSize: style.fontSize * 1.3,
        lineHeight: style.fontSize * 1.3 * lh,
      },
      header2: {
        fontSize: style.fontSize * 1.1,
        lineHeight: style.fontSize * 1.1 * lh,
      },
      header3: {
        fontSize: style.fontSize * 1.05,
        lineHeight: style.fontSize * 1.05 * lh,
      },
      listItem: {
        flexDirection: "row",
        flexWrap: "wrap",
      },
      bullet: {
        marginRight: style.fontSize * 0.66,
        lineHeight: style.fontSize * lh,
        fontSize: style.fontSize * 1.5,
      },
      // tags
      tagSuggestion: {
        backgroundColor: this.context.colors.fill,
        borderRadius: this.context.sizes.small,
        cursor: "pointer",
        paddingHorizontal: this.context.sizes.tiny,
      },
      // hr
      hr: {
        borderBottomWidth: 1,
        borderColor: this.context.colors.foreground,
        opacity: 0.5,
      },
      composerTooltip: {
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        flexDirection: "row",
        justifyContent: "flex-end",
        // backgroundColor: this.context.colors.highlight,
        // backgroundColor: this.context.colors.empty,
      },
      composerTooltipBackground: {
        zIndex: -1,
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // height: 200,
        // width: 200,
        backgroundColor: this.context.colors.empty,
        // backgroundColor: "red",
        borderBottomLeftRadius: this.context.sizes.borderRadius,
        borderBottomRightRadius: this.context.sizes.borderRadius,
      },
      composerTooltipContent: {
        padding: this.context.sizes.base,
        paddingRight: 0,
        flexDirection: "row",
        backgroundColor: this.context.colors.highlight,
        borderBottomLeftRadius: this.context.sizes.borderRadius,
        borderBottomRightRadius: this.context.sizes.borderRadius,
      },
      composerTooltipHat: {
        position: "absolute",
        left: 0,
        right: 0,
        top: -this.context.sizes.borderRadius,
        // height: this.context.sizes.borderRadius,
        borderTopRightRadius: this.context.sizes.borderRadius,
        borderTopLeftRadius: this.context.sizes.borderRadius,
        backgroundColor: this.context.colors.empty,
      },
    };
  };
}
